import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClock } from '@fortawesome/free-solid-svg-icons';
import useGetChangeSets from '../../api/useGetChangeSets';
import { ChangeSet } from '../../types/ChangeSetTypes';
import { ChangeSetStatusEnum } from '../../enums/ChangeSetStatusEnum';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import { MainContent } from '../../components/common/Layout.styles';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    ChangelogPage,
    FilterBar,
    FilterLabel,
    BorderedContainer,
    TableScrollContainer,
    HeaderRow,
    ChangeRow,
    EditSelect,
    EditInput,
    HideOverflow,
    TypeBadge,
    StatusBadge,
    LoadMoreButton,
    Error,
    NoData,
    UserCell,
    UserAvatar,
} from './ChangeLogPage.styles';
import { getInitials } from '../../utils/userFormatters';

// Use ChangeSet view model for UI display
type ChangeLogEntry = ChangeSet;

// localStorage key for persisting changelog filters
const CHANGELOG_FILTERS_KEY = 'changelog-filters';

interface ChangeLogFilters {
    selectedYear: number;
    selectedType: string;
    selectedCategory: string;
    selectedUser: string;
    selectedStatus: string;
    endDateFilter: string;
    selectedSort: string;
}

const INITIAL_LOAD = 200;
const LOAD_MORE_COUNT = 100;

const loadFilters = (currentYear: number): ChangeLogFilters => {
    try {
        const stored = localStorage.getItem(CHANGELOG_FILTERS_KEY);
        if (stored) {
            const parsed = JSON.parse(stored) as Partial<ChangeLogFilters>;
            return {
                selectedYear: parsed.selectedYear ?? currentYear,
                selectedType: parsed.selectedType ?? '',
                selectedCategory: parsed.selectedCategory ?? '',
                selectedUser: parsed.selectedUser ?? '',
                selectedStatus: parsed.selectedStatus ?? '',
                endDateFilter: parsed.endDateFilter ?? '',
                selectedSort: parsed.selectedSort ?? 'date-desc',
            };
        }
    } catch {
        // Ignore corrupted localStorage data
    }
    return {
        selectedYear: currentYear,
        selectedType: '',
        selectedCategory: '',
        selectedUser: '',
        selectedStatus: '',
        endDateFilter: '',
        selectedSort: 'date-desc',
    };
};

const saveFilters = (filters: ChangeLogFilters): void => {
    try {
        localStorage.setItem(CHANGELOG_FILTERS_KEY, JSON.stringify(filters));
    } catch {
        // Ignore quota errors
    }
};

// Generate list of years from current year back to 2020
const generateYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const years: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
        years.push(year);
    }
    return years;
};

const ChangeLogPage = () => {
    const currentYear = new Date().getFullYear();
    const initialFilters = useMemo(() => loadFilters(currentYear), [currentYear]);
    const [selectedYear, setSelectedYear] = useState<number>(initialFilters.selectedYear);
    const [selectedType, setSelectedType] = useState(initialFilters.selectedType);
    const [selectedCategory, setSelectedCategory] = useState(initialFilters.selectedCategory);
    const [selectedUser, setSelectedUser] = useState(initialFilters.selectedUser);
    const [selectedStatus, setSelectedStatus] = useState(initialFilters.selectedStatus);
    const [endDateFilter, setEndDateFilter] = useState(initialFilters.endDateFilter);
    const [selectedSort, setSelectedSort] = useState(initialFilters.selectedSort);
    const navigate = useNavigate();

    // Persist filters to localStorage whenever they change
    const persistFilters = useCallback(() => {
        saveFilters({ selectedYear, selectedType, selectedCategory, selectedUser, selectedStatus, endDateFilter, selectedSort });
    }, [selectedYear, selectedType, selectedCategory, selectedUser, selectedStatus, endDateFilter, selectedSort]);

    useEffect(() => {
        persistFilters();
    }, [persistFilters]);

    const yearOptions = useMemo(() => generateYearOptions(), []);

    // API integration
    const { changeSets, totalCount, loadedCount, loading, error, getChangeSets, reset } = useGetChangeSets();

    // Handle row click - navigate to detail page
    const handleRowClick = (id: number) => {
        navigate(`/changelog?changeLogId=${id}`);
    };

    // Initial load when year or sort changes — fresh fetch of 200 items
    useEffect(() => {
        reset();
        const sortOrder = selectedSort.endsWith('-asc') ? 'asc' : 'desc';
        getChangeSets({
            skip: 0,
            take: INITIAL_LOAD,
            sortBy: 'DateTimeUtc',
            sortOrder,
            year: selectedYear,
        });
    }, [getChangeSets, selectedYear, selectedSort, reset]);

    // Load more handler — appends next 100 items
    const handleLoadMore = () => {
        const sortOrder = selectedSort.endsWith('-asc') ? 'asc' : 'desc';
        getChangeSets({
            skip: loadedCount,
            take: LOAD_MORE_COUNT,
            sortBy: 'DateTimeUtc',
            sortOrder,
            year: selectedYear,
        });
    };

    const handleYearChange = (year: number) => {
        setSelectedYear(year);
    };

    const handleSortChange = (sort: string) => {
        setSelectedSort(sort);
    };

    // Use loaded data
    const allChangeLogEntries: ChangeLogEntry[] = Array.isArray(changeSets) ? changeSets : [];

    const types = ['Created', 'Deleted', 'Updated'];
    const categories = [
        'Unknown',
        'Product',
        'Procedure',
        'Safety Hazard',
        'Coating',
        'Coating Note',
        'Coating Manufacturer',
        'Draeger Tube',
        'Category Document',
        'USCG Group to Product',
        'Inter Product Compatibility',
        'Stowage Rule',
    ];
    const statuses = ['Pending Review', 'Reviewed'];
    const users = Array.from(new Set(allChangeLogEntries.map((entry) => entry.userName))).sort();

    // Client-side filtering on all loaded data
    const filteredEntries = useMemo(() => {
        return allChangeLogEntries.filter((entry) => {
            const matchesType = !selectedType || entry.typeName === selectedType;
            const matchesCategory = !selectedCategory || entry.categoryName === selectedCategory;
            const matchesUser = !selectedUser || entry.userName === selectedUser;
            const matchesStatus = !selectedStatus || entry.statusName === selectedStatus;

            let matchesDate = true;
            if (endDateFilter) {
                const filterDate = new Date(endDateFilter);
                filterDate.setHours(0, 0, 0, 0);
                matchesDate = entry.date >= filterDate;
            }

            return matchesType && matchesCategory && matchesUser && matchesStatus && matchesDate;
        });
    }, [selectedType, selectedCategory, selectedUser, selectedStatus, endDateFilter, allChangeLogEntries]);

    const hasMore = loadedCount < totalCount;

    return (
        <PageWrapper>
            <ChangelogPage as={PageContainer}>
                <MainContent>
                    {/* Error State */}
                    {error && <Error>Error loading change sets: {error}</Error>}

                    {/* Top bar: Year filter, Sort, and Load More */}
                    <FilterBar>
                        <ul>
                            <li>
                                <FilterLabel htmlFor="yearSelect">Filter by year:</FilterLabel>
                                <EditSelect
                                    id="yearSelect"
                                    value={selectedYear}
                                    onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
                                    aria-label="Filter by year"
                                >
                                    {yearOptions.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </EditSelect>
                            </li>
                            <li>
                                <FilterLabel htmlFor="sortSelect">Sort by:</FilterLabel>
                                <EditSelect
                                    id="sortSelect"
                                    value={selectedSort}
                                    onChange={(e) => handleSortChange(e.target.value)}
                                    aria-label="Sort order"
                                >
                                    <option value="date-desc">Date (newest first)</option>
                                    <option value="date-asc">Date (oldest first)</option>
                                </EditSelect>
                            </li>
                            <li className="spacer" />
                            <li className="load-more-info">
                                <span>{filteredEntries.length} of {totalCount}</span>
                            </li>
                            {hasMore && (
                                <li>
                                    <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
                                        {loading ? 'Loading...' : `Load ${LOAD_MORE_COUNT} more`}
                                    </LoadMoreButton>
                                </li>
                            )}
                        </ul>
                    </FilterBar>

                    {/* Table */}
                    <BorderedContainer>
                        <HeaderRow>
                            <div className="changeType">
                                <EditSelect value={selectedType} onChange={(e) => setSelectedType(e.target.value)} aria-label="Filter by change type">
                                    <option value="">Type</option>
                                    {types.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                            <div className="dateTime">
                                <EditInput
                                    type="date"
                                    gradientBackground
                                    value={endDateFilter}
                                    onChange={(e) => setEndDateFilter(e.target.value)}
                                    aria-label="Filter from date"
                                />
                            </div>
                            <div className="category">
                                <EditSelect
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    aria-label="Filter by category"
                                >
                                    <option value="">Category</option>
                                    {categories.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                            <div></div>
                            <div className="changedBy">
                                <EditSelect value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} aria-label="Filter by user">
                                    <option value="">User</option>
                                    {users.map((user) => (
                                        <option key={user} value={user}>
                                            {user}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                            <div className="changeStatus">
                                <EditSelect value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} aria-label="Filter by status">
                                    <option value="">Status</option>
                                    {statuses.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                        </HeaderRow>
                        <TableScrollContainer>
                            {loading && loadedCount === 0 && <LoadingSpinner text="Loading change sets..." />}
                            {!loading && !error && filteredEntries.length === 0 && <NoData>No change sets found.</NoData>}
                            {filteredEntries.length > 0 &&
                                filteredEntries.map((entry) => (
                                    <ChangeRow
                                        key={entry.id}
                                        clickable
                                        onClick={() => handleRowClick(entry.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && handleRowClick(entry.id)}
                                    >
                                        <div className="changeType">
                                            <TypeBadge variant={entry.typeName.toLowerCase() as 'created' | 'updated' | 'deleted'}>
                                                {entry.typeName}
                                            </TypeBadge>
                                        </div>
                                        <div className="dateTime">{formatDateTime(entry.date)}</div>
                                        <div className="category">{entry.categoryName}</div>
                                        <HideOverflow className="itemChanged">{entry.displayInformation}</HideOverflow>
                                        <div className="changedBy">
                                            <UserCell>
                                                <UserAvatar>{getInitials(entry.userName)}</UserAvatar>
                                                <HideOverflow>{entry.userName}</HideOverflow>
                                            </UserCell>
                                        </div>
                                        <div className="changeStatus">
                                            <StatusBadge status={entry.status === ChangeSetStatusEnum.Reviewed ? 'reviewed' : 'pending'}>
                                                <FontAwesomeIcon icon={entry.status === ChangeSetStatusEnum.Reviewed ? faCheck : faClock} />
                                                {entry.statusName}
                                            </StatusBadge>
                                        </div>
                                    </ChangeRow>
                                ))}
                            {loading && loadedCount > 0 && <LoadingSpinner text="Loading more..." />}
                        </TableScrollContainer>
                    </BorderedContainer>
                </MainContent>
            </ChangelogPage>
        </PageWrapper>
    );
};

export default ChangeLogPage;
